// Basic
import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsSession from '@/app/modules/session/api/actionsSession';
import actionsLogin from '../../api/actionsLogin';
// Common
import RouteUrls from '@/app/root/enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import LOGIN_SCREENS from '@/app/modules/login/enums/LoginScreens';
import userFields from '@/app/modules/users/monetic/fields/userFields';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import { Button } from '@/common-utils/components/ui/button';
// Lang
import { nsLogin } from '@/app/i18n/namespaces';

/**
 * @param {function} setHasMfaActivated
 * @param {function} setUserEmail
 * @param {function} setSubmitError
 * @param {function} exitScreen
 * @returns {JSX.Element} LoginForm component
 */
function LoginForm({ setHasMfaActivated, setUserEmail, setSubmitError, exitScreen }) {

    // Lang
    const { t: translate, i18n } = useTranslation();

    // Constants
    const passwordSchema = z
        .string()
        .min(1, { message: '' });

    const formSchema = z.object({
        email: z.string().email({ message: translate('form.validation.email.invalid') }),
        password: passwordSchema
    });

    // Store
    const { storeSession, storeToken, storeNewAccount } = useSessionStore();

    // Params
    const [searchParams] = useSearchParams();
    const forceLoginToken = searchParams.get('flt');

    // API Call
    const { data, isLoading: isLoadingSession, refetch } = actionsSession.getSession(false);

    const onSuccess = (userData) => {
        const token = userData?.data?.token ?? null;
        if (token) {
            setHasMfaActivated(true);
            exitScreen(LOGIN_SCREENS.MAIN_LOGIN);
            storeToken(token);
        }
        else {
            refetch();
        }
        // Store newAccount indication
        storeNewAccount(userData?.data?.[userFields.NEW_ACCOUNT.key] ?? false);
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR';
        setSubmitError(errorCode);
    };
    const submitLogin = actionsLogin.postLogin(onSuccess, onError);

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
            password: ''
        }
    });

    const onSubmit = (values) => {
        if (Object.keys(form.formState.errors).length === 0) {
            setSubmitError(null);
            submitLogin.mutate(values);
            setUserEmail(values.email);
        }
    };

    // Hooks
    // DidMount/DidUpdate
    useEffect(() => {
        if (!isLoadingSession && data && form.formState.isSubmitted) {
            storeSession(data);
        }
        if (forceLoginToken) {
            // Submit force login using FLT
            submitLogin.mutate({ token: forceLoginToken });
        }

    }, [isLoadingSession, data, form.formState.isSubmitted]);

    // Redirection if user is connected
    useEffect(() => {
        if (submitLogin.isSuccess) {
            return <Navigate to={RouteUrls.HOME.url} />;
        }
    }, [submitLogin.isSuccess]);

    // Trigger email validation on language change
    useEffect(() => {
        if (form.getFieldState('email').invalid) {
            form.trigger('email');
        }
    }, [i18n.language]);

    return (
        <Form {...form}>
            <form className='login-form space-y-4' onSubmit={form.handleSubmit(onSubmit)} data-cy='login-form'>
                <FormField
                    control={form.control}
                    name='email'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                <Entity namespace={nsLogin} entity='email' />
                            </FormLabel>
                            <FormControl>
                                <Input type='email' namespace={nsLogin} entity='email' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='password'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                <Entity namespace={nsLogin} entity='password' />
                            </FormLabel>
                            <FormControl>
                                <Input type='password' namespace={nsLogin} entity='password' {...field} />
                            </FormControl>
                        </FormItem>
                    )}
                />
                <div className='text-center'>
                    <Button
                        variant='primary'
                        type='submit'
                        entity={'button.signin'}
                        size='lg'
                        icon={ICONS.USER}
                        disabled={submitLogin.isLoading}
                    />
                </div>
            </form>
        </Form>
    );
}

LoginForm.propTypes = {
    setHasMfaActivated: PropTypes.func,
    setUserEmail: PropTypes.func,
    setSubmitError: PropTypes.func,
    exitScreen: PropTypes.func
};

export default LoginForm;